import { useEffect, useRef, useState } from "react";
import { MdWorkOutline } from "react-icons/md";
import { RiPencilLine, RiCloseCircleLine, RiSendPlaneLine, RiRefreshLine, RiEyeLine } from "react-icons/ri";
import { BsFillTrashFill } from "react-icons/bs";
import { usePermissions } from "../../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../../util/featureCodes";
import "./index.css";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/* job.publishedDate is "DD-MM-YYYY" — render it the way every reference
   product does: "22 Jul 2026". */
const formatDate = (ddmmyyyy) => {
  if (!ddmmyyyy) return null;
  const [d, m, y] = ddmmyyyy.split("-").map(Number);
  if (!d || !m || !y) return null;
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

/* Experience often comes back as a bare number (e.g. "5"). Append a "years"
   unit so it's clear it's years — unless the value already spells out a unit
   (years / yr / months / mo) itself. */
const formatExperience = (exp) => {
  if (exp === null || exp === undefined || String(exp).trim() === "") return "—";
  const str = String(exp).trim();
  if (/[a-zA-Z]/.test(str)) return str;
  return `${str} ${Number(str) === 1 ? "year" : "years"}`;
};

const PAGE_SIZE = 10;

/**
 * Inline row actions — visible View / Edit / Close / Publish / Reopen / Delete
 * icons (not a hidden menu), each wired to the same handlers JobListPage
 * already uses. Actions shown depend on the job's status.
 */
const JobRowActions = ({ job, onView, canOpenCard, onEdit, onClose, onPublish, onDelete, onReopen }) => {
  const { hasPermission } = usePermissions();
  const canEditJob = hasPermission(FEATURE_CODES.HIRING_JOB_EDIT);
  const canChangeStatus = hasPermission(FEATURE_CODES.HIRING_JOB_STATUS_CHANGE);
  const canDeleteJob = hasPermission(FEATURE_CODES.HIRING_JOB_DELETE);

  return (
    <div className="jt-actions" onClick={(e) => e.stopPropagation()}>
      {canOpenCard && (
        <button type="button" className="jt-action-btn jt-action-view" title="View" aria-label="View job" onClick={() => onView(job)}>
          <RiEyeLine />
        </button>
      )}
      {(job.status === "OPEN" || job.status === "DRAFT") && canEditJob && (
        <button type="button" className="jt-action-btn jt-action-edit" title="Edit" aria-label="Edit job" onClick={() => onEdit(job)}>
          <RiPencilLine />
        </button>
      )}
      {job.status === "OPEN" && canChangeStatus && (
        <button type="button" className="jt-action-btn jt-action-danger" title="Close Job" aria-label="Close job" onClick={() => onClose(job)}>
          <RiCloseCircleLine />
        </button>
      )}
      {job.status === "DRAFT" && (
        <>
          {canChangeStatus && (
            <button type="button" className="jt-action-btn jt-action-edit" title="Publish" aria-label="Publish job" onClick={() => onPublish(job)}>
              <RiSendPlaneLine />
            </button>
          )}
          {canDeleteJob && (
            <button type="button" className="jt-action-btn jt-action-danger" title="Delete" aria-label="Delete job" onClick={() => onDelete(job)}>
              <BsFillTrashFill />
            </button>
          )}
        </>
      )}
      {job.status === "CLOSED" && (
        <>
          {canChangeStatus && (
            <button type="button" className="jt-action-btn" title="Reopen" aria-label="Reopen job" onClick={() => onReopen(job)}>
              <RiRefreshLine />
            </button>
          )}
          {canDeleteJob && (
            <button type="button" className="jt-action-btn jt-action-danger" title="Delete" aria-label="Delete job" onClick={() => onDelete(job)}>
              <BsFillTrashFill />
            </button>
          )}
        </>
      )}
    </div>
  );
};

const JobRow = ({ job, onView, onEdit, onClose, onPublish, onDelete, onReopen }) => {
  const canOpenCard = job.status === "OPEN" || job.status === "CLOSED";
  const postedLabel = formatDate(job.publishedDate);

  return (
    <div
      className={canOpenCard ? "jt-row jt-row--clickable" : "jt-row"}
      onClick={() => canOpenCard && onView(job)}
      style={{ cursor: canOpenCard ? "pointer" : "default" }}
    >
      <div className="jt-cell jt-col-title">
        <p className="jt-title">{job.title}</p>
      </div>

      <div className="jt-cell jt-col-designation">
        <span className="jt-text">{job.designation || "—"}</span>
      </div>

      <div className="jt-cell jt-col-experience">
        <span className="jt-exp-value">{formatExperience(job.experience)}</span>
      </div>

      <div className="jt-cell jt-col-applicants">
        <span className="jt-stat-value">{job.totalApplications}</span>
      </div>

      <div className="jt-cell jt-col-shortlisted">
        <span className="jt-stat-value">{job.shortlisted}</span>
      </div>

      <div className="jt-cell jt-col-hired">
        <span className="jt-stat-value">{job.hired}</span>
      </div>

      <div className="jt-cell jt-col-rejected">
        <span className="jt-stat-value">{job.rejected}</span>
      </div>

      <div className="jt-cell jt-col-vacancies">
        <span className="jt-stat-value">{job.vacancies}</span>
      </div>

      <div className="jt-cell jt-col-date">
        <span className="jt-text">{postedLabel || "—"}</span>
      </div>

      <div className="jt-cell jt-col-actions">
        <JobRowActions
          job={job}
          onView={onView} canOpenCard={canOpenCard}
          onEdit={onEdit} onClose={onClose}
          onPublish={onPublish} onDelete={onDelete} onReopen={onReopen}
        />
      </div>
    </div>
  );
};

const SkeletonRow = () => (
  <div className="jt-row jt-skeleton-row">
    <div className="jt-cell jt-col-title"><div className="jt-skel jt-skel-title" /></div>
    <div className="jt-cell jt-col-designation"><div className="jt-skel jt-skel-sm" /></div>
    <div className="jt-cell jt-col-experience"><div className="jt-skel jt-skel-sm" /></div>
    <div className="jt-cell jt-col-applicants"><div className="jt-skel jt-skel-sm" /></div>
    <div className="jt-cell jt-col-shortlisted"><div className="jt-skel jt-skel-sm" /></div>
    <div className="jt-cell jt-col-hired"><div className="jt-skel jt-skel-sm" /></div>
    <div className="jt-cell jt-col-rejected"><div className="jt-skel jt-skel-sm" /></div>
    <div className="jt-cell jt-col-vacancies"><div className="jt-skel jt-skel-sm" /></div>
    <div className="jt-cell jt-col-date"><div className="jt-skel jt-skel-sm" /></div>
    <div className="jt-cell jt-col-actions"><div className="jt-skel jt-skel-dot" /></div>
  </div>
);

const EmptyState = ({ title, message }) => (
  <div className="jt-empty">
    <div className="jt-empty-illustration">
      <MdWorkOutline />
    </div>
    <p className="jt-empty-title">{title}</p>
    <p className="jt-empty-message">{message}</p>
  </div>
);

const TableHeader = () => (
  <div className="jt-header">
    <span className="jt-col-title">JOB TITLE</span>
    <span className="jt-col-designation">DESIGNATION</span>
    <span className="jt-col-experience">EXPERIENCE</span>
    <span className="jt-col-applicants jt-col-secondary">APPLICANTS</span>
    <span className="jt-col-shortlisted jt-col-secondary">SHORTLISTED</span>
    <span className="jt-col-hired jt-col-secondary">HIRED</span>
    <span className="jt-col-rejected jt-col-secondary">REJECTED</span>
    <span className="jt-col-vacancies">VACANCIES</span>
    <span className="jt-col-date">POSTED DATE</span>
    <span className="jt-col-actions">ACTIONS</span>
  </div>
);

const JobsTable = ({
  jobs, loading, resetKey,
  onView, onEdit, onClose, onPublish, onDelete, onReopen,
  emptyTitle, emptyMessage,
}) => {
  const [page, setPage] = useState(1);
  const prevResetKey = useRef(resetKey);

  useEffect(() => {
    if (prevResetKey.current !== resetKey) {
      prevResetKey.current = resetKey;
      setPage(1);
    }
  }, [resetKey]);

  const total = jobs.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = total === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(safePage * PAGE_SIZE, total);
  const pageJobs = jobs.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (loading) {
    return (
      <div className="jt-card">
        <div className="jt-scroll">
          <TableHeader />
          {Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)}
        </div>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="jt-card">
        <div className="jt-scroll">
          <TableHeader />
          <EmptyState title={emptyTitle} message={emptyMessage} />
        </div>
      </div>
    );
  }

  return (
    <div className="jt-card">
      <div className="jt-scroll">
        <TableHeader />

        {pageJobs.map((job) => (
          <JobRow
            key={job.id}
            job={job}
            onView={onView} onEdit={onEdit} onClose={onClose}
            onPublish={onPublish} onDelete={onDelete} onReopen={onReopen}
          />
        ))}
      </div>

      <div className="jt-pagination">
        <span className="jt-pagination-summary">
          Showing <strong>{pageStart}</strong>–<strong>{pageEnd}</strong> of <strong>{total}</strong> jobs
        </span>
        <div className="jt-pagination-controls">
          <button
            type="button"
            className="jt-page-btn"
            disabled={safePage === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              className={n === safePage ? "jt-page-btn jt-page-btn-active" : "jt-page-btn"}
              onClick={() => setPage(n)}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            className="jt-page-btn"
            disabled={safePage === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobsTable;
